import { ReactNode, createContext, useState } from 'react';
import { useDisclosure } from '@chakra-ui/react';
import { Node } from 'reactflow';
import { RouterOutputs } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { useUndoRedo } from './useUndoRedo';

function useFlowEditorState() {
  const {
    getButtonProps: simulationButtonProps,
    getDisclosureProps: simulationDisclosure,
    isOpen: simulationIsOpen,
  } = useDisclosure();

  const [hiddenSimulation, setHiddenSimulation] = useState(!simulationIsOpen);

  const [activeNode, setActiveNode] = useState<Node | null>(null);
  const [lastPublishedResourceVersion, setLastPublishedResourceVersion] = useState<Omit<
    NonNullable<RouterOutputs['resourceVersions']['byId']>,
    'fileUrls'
  > | null>(null);

  const { undo, redo, canUndo, canRedo, commitSnapshot } = useUndoRedo();

  return {
    simulationButtonProps,
    simulationDisclosure,
    simulationIsOpen,
    setActiveNode,
    activeNode,
    hiddenSimulation,
    setHiddenSimulation,
    undo,
    redo,
    canUndo,
    canRedo,
    commitSnapshot,
    lastPublishedResourceVersion,
    setLastPublishedResourceVersion,
  };
}

export const FlowEditorStateContext = createContext<ReturnType<typeof useFlowEditorState>>(
  {} as any,
);

// The active node on its own, for consumers that only care about selection
// (e.g. every rendered edge). FlowEditorStateContext's value object is rebuilt
// on each provider render, so subscribing to the full context from per-edge
// components would re-render them all whenever any editor state changes.
export const ActiveNodeContext = createContext<Node | null>(null);

export function FlowEditorStateProvider({ children }: { children: ReactNode }) {
  const flowEditorState = useFlowEditorState();
  return (
    <FlowEditorStateContext.Provider value={flowEditorState}>
      <ActiveNodeContext.Provider value={flowEditorState.activeNode}>
        {children}
      </ActiveNodeContext.Provider>
    </FlowEditorStateContext.Provider>
  );
}
